
import React, { useState } from 'react';
import { LogOut, LayoutGrid, Activity, AlertCircle, ArrowUpCircle, CheckCircle2, Search } from 'lucide-react';
import { ClientEntity } from '../types';
import { useStore } from '../store/useStore';

interface AgencyDashboardProps {
  clients: ClientEntity[];
  coverImages: Record<string, string>;
  onLogout: () => void;
}

export const AgencyDashboard: React.FC<AgencyDashboardProps> = ({ clients, coverImages, onLogout }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { appSettings } = useStore();
  
  const filteredClients = clients.filter(c => 
    c.data.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [currentTime] = useState(() => Date.now());

  // Mock indicators based on ID length or date to make them look dynamic
  const getHealthStatus = (client: ClientEntity, index: number) => {
    // Usando o store real para o primeiro cliente (Demo), mock sutil para os demais caso existam
    const lastLogin = index === 0 ? new Date(appSettings.lastLoginAt).getTime() : currentTime - (index * 86400000 * 2);
    const daysSinceLogin = Math.floor((currentTime - lastLogin) / (1000 * 60 * 60 * 24));
    
    if (daysSinceLogin > 7) return { status: 'risk', text: 'Risco Alto', days: daysSinceLogin, color: 'text-rose-600', bg: 'bg-rose-50' };
    if (daysSinceLogin > 3) return { status: 'warning', text: 'Atenção', days: daysSinceLogin, color: 'text-amber-600', bg: 'bg-amber-50' };
    return { status: 'healthy', text: 'Saudável', days: daysSinceLogin, color: 'text-emerald-600', bg: 'bg-emerald-50' };
  };

  const getUpsellOpportunity = (client: ClientEntity, index: number) => {
    const leadsGenerated = index === 0 ? appSettings.totalLeadsGenerated : 100 * index;
    if (leadsGenerated > 900) return true;
    return false;
  };

  return (
    <div className="flex h-screen bg-[#FAFAFA] text-gray-900 overflow-hidden font-sans">
      
      {/* SIDEBAR CLEAN */}
      <aside className="relative z-20 flex flex-col bg-white border-r border-gray-200 h-full w-[280px]">
        <div className="h-16 flex items-center px-6 border-b border-gray-100">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-8 h-8 rounded bg-black flex items-center justify-center">
              <span className="text-white font-bold text-lg leading-none">D</span>
            </div>
            <div>
              <h1 className="text-sm font-semibold tracking-tight text-gray-900">DithoSites</h1>
              <p className="text-[10px] tracking-widest text-indigo-600 uppercase font-bold">Agency Hub</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
          <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-100 text-gray-900 font-medium text-sm transition-colors">
            <Activity size={18} />
            Monitoramento
          </button>
        </div>

        <div className="p-4 border-t border-gray-100">
          <button 
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 font-medium text-sm transition-all"
          >
            <LogOut size={16} />
            Sair do Painel
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 relative flex flex-col h-full overflow-hidden bg-[#FAFAFA]">
        <div className="h-16 border-b border-gray-200 bg-white px-8 flex items-center justify-between shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Health Score & Monitoramento Comportamental</h2>
          <div className="relative w-64">
             <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
             <input 
               type="text" 
               placeholder="Buscar cliente..." 
               value={searchTerm}
               onChange={e => setSearchTerm(e.target.value)}
               className="w-full pl-9 pr-4 py-1.5 border border-gray-200 rounded-lg text-sm focus:border-black focus:ring-1 focus:ring-black outline-none transition-all"
             />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-5xl mx-auto space-y-6">
             
             {/* Info Cards */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
               <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                 <div className="flex items-center gap-2 mb-2">
                   <Activity size={20} className="text-indigo-600" />
                   <h3 className="font-semibold text-gray-900">Visão Geral</h3>
                 </div>
                 <p className="text-sm text-gray-500">O sistema monitora a atividade real (logins e leads) dos clientes e te avisa antes deles cancelarem.</p>
               </div>
               
               <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 shadow-sm">
                 <div className="flex items-center gap-2 mb-2">
                   <AlertCircle size={20} className="text-rose-600" />
                   <h3 className="font-semibold text-rose-900">Risco de Churn (Cancelamento)</h3>
                 </div>
                 <p className="text-sm text-rose-700">Aparece para clientes que não abrem o sistema há mais de 7 dias.</p>
               </div>

               <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 shadow-sm">
                 <div className="flex items-center gap-2 mb-2">
                   <ArrowUpCircle size={20} className="text-emerald-600" />
                   <h3 className="font-semibold text-emerald-900">Gatilho de Upsell</h3>
                 </div>
                 <p className="text-sm text-emerald-700">Aparece quando o cliente bate o teto do plano e a máquina já engatilhou a oferta.</p>
               </div>
             </div>

             {/* Clients Table */}
             <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm text-gray-600">
                  <thead className="bg-gray-50 border-b border-gray-200 font-medium text-gray-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-4">Cliente / Projeto</th>
                      <th className="px-6 py-4">Health Score</th>
                      <th className="px-6 py-4">Status de Uso</th>
                      <th className="px-6 py-4">Ação Recomendada</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredClients.map((client, index) => {
                      const health = getHealthStatus(client, index);
                      const isUpsell = getUpsellOpportunity(client, index);

                      return (
                        <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {coverImages[client.id] ? (
                                <img src={coverImages[client.id]} alt="" className="w-10 h-10 rounded-md object-cover border border-gray-200" />
                              ) : (
                                <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center">
                                  <LayoutGrid size={16} className="text-gray-400" />
                                </div>
                              )}
                              <div>
                                <h4 className="font-bold text-gray-900">{client.data.name}</h4>
                                <p className="text-xs text-gray-500">ID: {client.id.substring(0, 8)}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${health.bg} ${health.color}`}>
                                {health.text}
                              </span>
                              <span className="text-[11px] text-gray-500">Sem login há {health.days} dias</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {isUpsell ? (
                              <div className="flex items-center gap-1.5 text-emerald-600 font-medium">
                                <ArrowUpCircle size={16} />
                                Bateu Teto do Plano
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-gray-500">
                                <CheckCircle2 size={16} />
                                Uso Normal
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {health.status === 'risk' && (
                              <button className="bg-black text-white hover:bg-gray-800 text-xs px-3 py-1.5 rounded font-medium transition-colors">
                                Enviar WhatsApp
                              </button>
                            )}
                            {isUpsell && health.status !== 'risk' && (
                              <button className="bg-emerald-600 text-white hover:bg-emerald-700 text-xs px-3 py-1.5 rounded font-medium transition-colors">
                                Acompanhar Upsell
                              </button>
                            )}
                            {health.status === 'healthy' && !isUpsell && (
                              <span className="text-gray-400 text-xs font-medium">Nenhuma ação</span>
                            )}
                            {health.status === 'warning' && !isUpsell && (
                              <span className="text-amber-600 text-xs font-medium">Monitorar</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                
                {filteredClients.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Nenhum cliente encontrado.</p>
                  </div>
                )}
             </div>

          </div>
        </div>
      </main>
    </div>
  );
};
